import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { PriceBreakdown } from "@/components/site/PriceBreakdown";
import { api, errMsg, money } from "@/lib/api";
import { StatusPill, displayStatus } from "@/components/admin/status";

const Row = ({ k, v, testid }) => <div className="flex justify-between gap-4 py-1.5 text-sm border-b border-border"><span className="text-muted-foreground">{k}</span><span className="text-right" data-testid={testid}>{v}</span></div>;

const ConfirmDialog = ({ open, setOpen, title, desc, children, onConfirm, busy, testid }) => (
  <Dialog open={open} onOpenChange={setOpen}>
    <DialogContent data-testid={testid}>
      <DialogTitle>{title}</DialogTitle><DialogDescription>{desc}</DialogDescription>
      {children}
      <div className="flex justify-end gap-3"><button onClick={() => setOpen(false)} className="text-sm px-3">Back</button>
        <button onClick={onConfirm} disabled={busy} className="bg-destructive text-white px-5 py-2 rounded-full text-sm disabled:opacity-60" data-testid={`${testid}-confirm`}>{busy ? "Working…" : "Confirm"}</button></div>
    </DialogContent>
  </Dialog>
);

export const BookingDetail = ({ booking: b, onClose, onChanged }) => {
  const [notes, setNotes] = useState("");
  const [dlg, setDlg] = useState("");
  const [refundFull, setRefundFull] = useState(false);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (b) { setNotes(b.admin_notes || ""); setAmount(String(Math.max(0, (b.amount_paid || 0) - (b.amount_refunded || 0)).toFixed(2))); setReason(""); setRefundFull(false); }
  }, [b]);
  if (!b) return null;
  const remaining = (b.amount_paid || 0) - (b.amount_refunded || 0);
  const act = async (fn, ok, close = true) => {
    setBusy(true);
    try { await fn(); toast.success(ok); setDlg(""); onChanged(close); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const saveNotes = () => act(() => api.patch(`/admin/bookings/${b.id}`, { admin_notes: notes }), "Notes saved", false);
  const cancel = () => act(() => api.post(`/admin/bookings/${b.id}/cancel`, { refund: refundFull, reason }), "Reservation cancelled");
  const refund = () => act(() => api.post(`/admin/bookings/${b.id}/refund`, { amount: Number(amount), reason }), "Refund submitted to Stripe");
  return (
    <Sheet open={!!b} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto bg-background" data-testid="booking-detail">
        <SheetTitle className="font-serif text-3xl">{b.ref}</SheetTitle>
        <div className="flex gap-2 mt-2"><StatusPill value={displayStatus(b)} /><StatusPill value={b.payment_status} />{b.test_mode && <span className="text-[10px] uppercase self-center text-[hsl(30_60%_35%)]">Test mode record</span>}</div>
        <div className="mt-5">
          <Row k="Guest" v={b.name} /><Row k="Email" v={b.email} /><Row k="Phone" v={b.phone} /><Row k="Guests" v={b.guests} />
          {b.note && <Row k="Guest note" v={b.note} />}
          <Row k="Booked" v={new Date(b.created_at).toLocaleString()} />
        </div>
        <div className="mt-5 border border-border rounded-lg bg-white p-4"><PriceBreakdown quote={{ ...b.quote, is_sample: false }} /></div>
        <div className="mt-5">
          <Row k="Amount paid" v={money(b.amount_paid)} testid="detail-amount-paid" /><Row k="Refunded" v={money(b.amount_refunded)} />
          <Row k="Stripe payment" v={b.payment_intent || "None"} />
          {b.payment_failures > 0 && <Row k="Failed attempts" v={`${b.payment_failures} (${b.last_payment_error})`} />}
          {b.cancel_reason && <Row k="Cancel reason" v={b.cancel_reason} />}
        </div>
        <div className="mt-5 space-y-2">
          <p className="text-sm font-semibold">Internal notes</p>
          <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="detail-notes-input" />
          <button onClick={saveNotes} disabled={busy} className="border border-border px-4 py-1.5 rounded-full text-sm" data-testid="detail-save-notes">Save notes</button>
        </div>
        <div className="mt-6 flex flex-wrap gap-3">
          {["pending", "confirmed"].includes(b.status) && <button onClick={() => setDlg("cancel")} className="border border-destructive text-destructive px-4 py-2 rounded-full text-sm" data-testid="detail-cancel-button">Cancel reservation</button>}
          {remaining > 0.001 && <button onClick={() => setDlg("refund")} className="border border-border px-4 py-2 rounded-full text-sm" data-testid="detail-refund-button">Issue refund</button>}
        </div>
        <ConfirmDialog open={dlg === "cancel"} setOpen={(o) => setDlg(o ? "cancel" : "")} title={`Cancel ${b.ref}?`} desc="The dates are released and the guest is emailed. Nothing is refunded unless you tick the box." onConfirm={cancel} busy={busy} testid="cancel-dialog">
          <Input placeholder="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="cancel-reason-input" />
          {b.status === "confirmed" && remaining > 0.001 && <label className="flex gap-2 text-sm items-center"><Checkbox checked={refundFull} onCheckedChange={setRefundFull} data-testid="cancel-refund-checkbox" /> Also refund the remaining {money(remaining)} via Stripe</label>}
        </ConfirmDialog>
        <ConfirmDialog open={dlg === "refund"} setOpen={(o) => setDlg(o ? "refund" : "")} title="Issue a refund" desc={`Up to ${money(remaining)} can be refunded to the guest's card through Stripe.`} onConfirm={refund} busy={busy} testid="refund-dialog">
          <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="refund-amount-input" />
          <Input placeholder="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="refund-reason-input" />
        </ConfirmDialog>
      </SheetContent>
    </Sheet>
  );
};
